import React, { useEffect, useMemo, useState } from "react";
import { QrCode, TextAa, Wrench, Percent, List, X, Paragraph } from "@phosphor-icons/react";
import { MantineProvider, Text, createTheme } from "@mantine/core";
import QrPage from "./pages/QrPage";
import TrCaseConverterPage from "./pages/TrCaseConverterPage";
import KdvPage from "./pages/KdvPage";
import RemoveLineBreaksPage from "./pages/RemoveLineBreaksPage";

type PageKey = "qr" | "tr-case" | "kdv" | "remove-line-breaks";

const pageConfig: Record<
  PageKey,
  {
    label: string;
    icon: React.ComponentType<{
      size?: number;
      weight?: "duotone" | "fill" | "regular";
    }>;
    component: React.ReactNode;
  }
> = {
  qr: {
    label: "QR Oluşturucu",
    icon: QrCode,
    component: <QrPage />,
  },
  "tr-case": {
    label: "TR Kelime Dönüştürücü",
    icon: TextAa,
    component: <TrCaseConverterPage />,
  },
  kdv: {
    label: "KDV Hesaplama",
    icon: Percent,
    component: <KdvPage />,
  },
  "remove-line-breaks": {
    label: "Satır Sonlarını Kaldır",
    icon: Paragraph,
    component: <RemoveLineBreaksPage />,
  },
};

function getHashPage(): PageKey {
  const hash = window.location.hash.replace("#", "");
  return hash in pageConfig ? (hash as PageKey) : "qr";
}

const theme = createTheme({
  primaryColor: "blue",
  defaultRadius: "md",
});

export default function App() {
  const [activePage, setActivePage] = useState<PageKey>(getHashPage);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const onHashChange = () => setActivePage(getHashPage());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  function navigate(page: PageKey) {
    window.location.hash = page;
    setActivePage(page);
    setSidebarOpen(false);
  }

  const renderedPage = useMemo(
    () => pageConfig[activePage].component,
    [activePage],
  );

  const activeItem = pageConfig[activePage];

  return (
    <MantineProvider theme={theme}>
      <div className="app-shell">
        {/* Mobile Header */}
        <header className="mobile-header">
          <button
            type="button"
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Menüyü aç/kapat"
          >
            {sidebarOpen ? <X size={22} /> : <List size={22} />}
          </button>
          <div className="mobile-header-title">
            <activeItem.icon size={18} weight="fill" />
            <Text fw={600} size="sm">{activeItem.label}</Text>
          </div>
        </header>

        {/* Backdrop for mobile when sidebar is open */}
        {sidebarOpen && (
          <div
            className="sidebar-backdrop"
            onClick={() => setSidebarOpen(false)}
            aria-hidden
          />
        )}

        <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="sidebar-logo">
            <Wrench size={20} weight="fill" />
            <Text fw={700} size="sm">cTools</Text>
          </div>

          <nav className="sidebar-nav">
            <Text className="sidebar-section-label">Araçlar</Text>
            {(Object.keys(pageConfig) as PageKey[]).map((pageKey) => {
              const isActive = activePage === pageKey;
              const item = pageConfig[pageKey];
              const Icon = item.icon;

              return (
                <button
                  key={pageKey}
                  className={`sidebar-item ${isActive ? "active" : ""}`}
                  onClick={() => navigate(pageKey)}
                  type="button"
                >
                  <Icon size={18} weight={isActive ? "fill" : "regular"} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="page-content">{renderedPage}</main>
      </div>
    </MantineProvider>
  );
}
